/**
 * FundabilityPath — 5-step process timeline
 * Design: White background, navy steps, green connector line
 */

import React from "react";
import { Search, MessageSquare, FileText, Handshake, DollarSign } from "lucide-react";

const steps = [
  {
    icon: Search,
    step: "01",
    title: "Instant Prequalification",
    time: "2 minutes",
    description: "Complete our quick online form. No credit impact, no commitment. Get instant results on your eligibility.",
    color: "#48d480",
  },
  {
    icon: MessageSquare,
    step: "02",
    title: "Expert Strategy Session",
    time: "Same day",
    description: "Meet with a dedicated funding specialist who reviews your options and creates a personalized funding strategy.",
    color: "#48d480",
  },
  {
    icon: FileText,
    step: "03",
    title: "Full Application",
    time: "1-2 days",
    description: "We handle the paperwork. Our team prepares and submits your complete application with all required documentation.",
    color: "#48d480",
  },
  {
    icon: Handshake,
    step: "04",
    title: "Lender Placement",
    time: "2-5 days",
    description: "We match you with the best lenders from our network of 100+ funding partners and negotiate on your behalf.",
    color: "#48d480",
  },
  {
    icon: DollarSign,
    step: "05",
    title: "Funding in Your Account",
    time: "24-48 hours",
    description: "Once approved, funds are deposited directly into your business account. Time to grow your business!",
    color: "#e8821c",
  },
];

export default function FundabilityPath() {
  return (
    <section id="fundability-path" data-testid="fundability-path" className="py-20 md:py-28 bg-white">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16 fade-up">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#48d480]" />
            <span className="section-label">How It Works</span>
            <div className="h-px w-8 bg-[#48d480]" />
          </div>
          <h2
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-3xl md:text-4xl font-bold text-[#0f2d52]"
          >
            The Fundability Path
          </h2>
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-gray-500 mt-3 max-w-xl mx-auto"
          >
            From application to funding in as little as 5 business days. Here's exactly how we make it happen.
          </p>
        </div>

        {/* Desktop Timeline */}
        <div className="hidden md:block relative">
          {/* Connector Line */}
          <div className="absolute top-16 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-[#48d480] via-[#48d480] to-[#e8821c]" />

          <div className="grid grid-cols-5 gap-4">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="flex flex-col items-center text-center fade-up" style={{ transitionDelay: `${idx * 100}ms` }}>
                  {/* Circle */}
                  <div
                    className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center mb-6 shadow-lg"
                    style={{ backgroundColor: step.color }}
                  >
                    <Icon size={22} className="text-[#0f2d52]" />
                  </div>
                  {/* Step number */}
                  <div
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-xs font-black tracking-widest text-[#48d480] mb-2"
                  >
                    STEP {step.step}
                  </div>
                  <h3
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-[#0f2d52] font-bold text-base mb-1"
                  >
                    {step.title}
                  </h3>
                  <div
                    className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-3"
                    style={{
                      backgroundColor: step.color + "20",
                      color: step.color === "#e8821c" ? "#c96a0f" : "#1a7a4a",
                      fontFamily: "'Inter', sans-serif"
                    }}
                  >
                    {step.time}
                  </div>
                  <p
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-gray-500 text-xs leading-relaxed"
                  >
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="md:hidden space-y-6">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div key={step.step} className="flex gap-5 fade-up">
                <div className="flex flex-col items-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-md"
                    style={{ backgroundColor: step.color }}
                  >
                    <Icon size={20} className="text-[#0f2d52]" />
                  </div>
                  {idx < steps.length - 1 && (
                    <div className="w-0.5 flex-1 mt-2 bg-gradient-to-b from-[#48d480] to-[#48d480]/30" />
                  )}
                </div>
                <div className="pb-6">
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-xs font-black tracking-widest text-[#48d480] mb-1">STEP {step.step}</div>
                  <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-base mb-1">{step.title}</h3>
                  <span
                    className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-2"
                    style={{
                      backgroundColor: step.color + "20",
                      color: step.color === "#e8821c" ? "#c96a0f" : "#1a7a4a",
                      fontFamily: "'Inter', sans-serif"
                    }}
                  >
                    {step.time}
                  </span>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-14 fade-up">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-[#f8fafc] border border-gray-200 rounded-2xl p-6">
            <div className="text-left">
              <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-lg">Ready to get started?</div>
              <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm">Takes less than 2 minutes. No credit impact.</div>
            </div>
            <a href="#funding-finder" className="btn-primary whitespace-nowrap">
              Check My Eligibility
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
